/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { getDashboardEnvironment } from "../../utils/functions/client-code";

export type ClientType = {
  client_code: string;
  client_name: string;
  client_logo: string;
};

type User = {
  _id: string;
  name?: string;
  email: string;
  phone_number?: string;
  profile_picture?: string;
  country_code?: string;
  client_code?: string;
  role: string;
  client_list?: ClientType[];
  current_client_code?: string;
  org_code: string | null;
  is_agent?: boolean;
  helpdesk_category_access?: string[] | null;
  helpdesk_category_update_access?: string[] | null;
  properyIds?: string[] | null;
  // TEMP: verbaiq-user-gate — remove once full RBAC ships
  verbaiq_chat_access_client_codes?: string[] | null;
  verbaiq_analytics_access_client_codes?: string[] | null;
};

export type OutlookIntegration = {
  is_integration_available: boolean;
  integration_status: boolean;
};

export type AuthState = {
  user: User | null;
  client: Client | null;
  organization: string | null;
  isVerified: boolean;
  isLoggedIn: boolean;
  isOrgView: boolean;
  outlookIntegration: OutlookIntegration | null;
};

export type Client = {
  client_code: string;
  client_name: string;
  client_logo: string;
  region?: string;
  lead_delete: {
    enabled: boolean;
    allowed_roles: string[];
  };
  email_signature?: {
    html_content: string;
  };
  widget_config: {
    suggested_questions: string[];
    // The client's brand colour (client_configs.widget_config.accent_color) — already served by
    // /client_config. The analytics board derives its "Brand" palette from this hue.
    accent_color?: string;
  };
  campaign_config: {
    campaign_locked: boolean;
    allowed_roles: string[];
    subscribed_channels: {
      call: boolean;
      email: boolean;
      whatsapp: boolean;
      sms: boolean;
    };
  };
  live_communication_channels?: {
    [key: string]: boolean;
  };
  analytics_dummy_data: any;
  verbaiq_config?: {
    enabled?: boolean;
    allowed_roles?: string[];
    // Two-surface access control (chat vs analytics). Falls back to the legacy
    // enabled/allowed_roles above when absent.
    verbaiq_chat_enabled?: boolean;
    verbaiq_analytics_enabled?: boolean;
    verbaiq_chat_allowed_roles?: string[];
    verbaiq_analytics_allowed_roles?: string[];
    show_cost?: boolean;
    show_usage?: boolean;
    // Master switch for the usage-limiting feature (seats/tiers/token-limits/blocking + every
    // usage-based UI surface). Defaults False server-side — as of 2026-08 usage is open to all
    // users; nothing here should render unless this is explicitly true.
    usage_enforcement_enabled?: boolean;
    starter_prompts?: string[];
  };
  dashboard_config: {
    feature_lock?: {
      billing_locked?: boolean;
      pms_locked?: boolean;
      analytics_locked?: boolean;
      crm_locked?: boolean;
      communication_locked?: boolean;
      user_management_locked?: boolean;
      settings_locked?: boolean;
      ticket_locked?: boolean;
      alert_locked?: boolean;
      kam_locked?: boolean;
      user_manual_locked?: boolean;
      vf_copilot_locked?: boolean;
    };
    communication_lock?: {
      chat_locked?: boolean;
      call_locked?: boolean;
      mail_locked?: boolean;
      whatsapp_locked?: boolean;
      sms_locked?: boolean;
    };
    multi_faq_config?: {
      enabled?: boolean;
    };
    crm_lock?: {
      crm_locked?: boolean;
    };
    playground_config?: {
      enabled?: boolean;
      allowed_roles?: string[];
    };
    crm_lead_details_config?: {
      tabs?: Array<{ key: string; label: string }>;
    };
  };
  lead_inbox_display_config?: {
    sections: Array<{
      section: string;
      enabled: boolean;
      subsections: {
        [key: string]: {
          is_enabled: boolean;
          display_order: number;
          is_mandatory: boolean;
        };
      } | null;
    }>;
  };
  industry?: string[];
};

const initialState: AuthState = {
  user: null,
  client: null,
  organization: null,
  isVerified: false,
  isLoggedIn: false,
  isOrgView: localStorage.getItem("is_org_view") === "true",
  outlookIntegration: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    login: (state, action) => {
      const user: User = action.payload.user;
      const client: Client = action.payload.client;
      const organization = action.payload.organization || null;
      const isVerified = action.payload.isVerified || false;
      if (user?.client_list && user?.client_list?.length > 0) {
        localStorage.setItem(
          "client_list",
          JSON.stringify(user?.client_list ?? [])
        );
      }

      if (user?.client_code) {
        localStorage.setItem("default_client_code", user?.client_code);
      }
      state.isLoggedIn = true;
      state.user = user;
      state.client = client;
      state.organization = organization;
      state.isVerified = isVerified;
    },
    logout: (state) => {
      state.user = null;
      state.client = null;
      state.organization = null;
      state.isVerified = false;
      state.isLoggedIn = false;
      state.isOrgView = false;
      state.outlookIntegration = null;

      const appearanceMode = localStorage.getItem("appearanceMode");
      const isDarkMode = localStorage.getItem("isDarkMode");
      localStorage.clear();
      window.sessionStorage.clear();

      if (appearanceMode) {
        localStorage.setItem("appearanceMode", appearanceMode);
      }
      if (isDarkMode) {
        localStorage.setItem("isDarkMode", isDarkMode);
      }
      const cookieOptions: Cookies.CookieAttributes = {};
      const env = getDashboardEnvironment();
      if (env !== "development") {
        cookieOptions.domain = import.meta.env.VITE_BASE_ORIGIN_URL;
      }
      Cookies.remove("userId", cookieOptions);
      Cookies.remove("auth_token");
    },
    setOrganization: (state, action) => {
      state.organization = action.payload;
      localStorage.setItem(
        "userDetails",
        JSON.stringify({
          ...state,
          organization: action.payload,
        })
      );
      localStorage.setItem(
        "organization",
        JSON.stringify({
          organization: action.payload,
        })
      );
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
    updateClient: (state, action) => {
      state.client = { ...state.client, ...action.payload };
    },
    setOutlookIntegration: (state, action) => {
      state.outlookIntegration = action.payload;
    },
    setOrgView: (state, action) => {
      state.isOrgView = action.payload;
      if (action.payload) {
        localStorage.setItem("is_org_view", "true");
      } else {
        localStorage.removeItem("is_org_view");
      }
    },
  },
});

export const {
  login,
  logout,
  setOrganization,
  updateUser,
  updateClient,
  setOutlookIntegration,
  setOrgView,
} = authSlice.actions;
